import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  projectService,
  type ConversationSummaryDto,
  type DiscussionRoomDto,
} from '../api/services/project.service';

/**
 * Startup-level Discussion Room data (mirrors useScopeConversations). The room
 * is get-or-created once per startup; its conversation list is the "Discussions"
 * sidebar list. Keyed by startup so every route under /discussions shares the
 * cache.
 */
export const discussionRoomKey = (startupId?: string | null) =>
  ['discussion-room', startupId ?? ''] as const;

export const discussionConversationsKey = (
  startupId?: string | null,
  roomId?: string | null,
) => ['discussion-conversations', startupId ?? '', roomId ?? ''] as const;

/** Get-or-create the startup's single Discussion Room. */
export function useDiscussionRoom(startupId?: string | null) {
  return useQuery<DiscussionRoomDto>({
    queryKey: discussionRoomKey(startupId),
    queryFn: () => projectService.getDiscussionRoom(startupId as string),
    enabled: !!startupId,
    staleTime: 5 * 60_000,
  });
}

/** Conversations inside a Discussion Room. */
export function useDiscussionConversations(
  startupId?: string | null,
  roomId?: string | null,
) {
  return useQuery<ConversationSummaryDto[]>({
    queryKey: discussionConversationsKey(startupId, roomId),
    queryFn: () =>
      projectService.listDiscussionConversations(startupId as string, roomId as string),
    enabled: !!startupId && !!roomId,
    staleTime: 30_000,
  });
}

/** Invalidate a room's conversation list after create / rename / delete. */
export function useInvalidateDiscussionConversations() {
  const queryClient = useQueryClient();
  return useCallback(
    (startupId?: string | null, roomId?: string | null) =>
      queryClient.invalidateQueries({
        queryKey: discussionConversationsKey(startupId, roomId),
      }),
    [queryClient],
  );
}
